import { useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSupportTicketAttachmentList } from "@/components/HubSupportTicketAttachmentList";
import {
  HubSupportTicketMessage,
  HubSupportTicketMessageThread,
  HubSupportTicketStatusBadge,
} from "@/components/HubSupportTicketMessageThread";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import {
  hubSupportTicketAttachmentApiPayload,
  type HubSupportLocalAttachment,
  type HubSupportTicketAttachmentView,
} from "@/lib/hubSupportTicketAttachments";
import { hubSupportTicketFieldLabelClassName, hubSupportTicketReporterFieldCopy } from "@/lib/hubSupportTicketFieldCopy";
import { formatTimestamp } from "@/lib/utils";

const PAGE_HELP =
  "Bug reports and suggestions you submitted to the hub team. Open a ticket to see status, resolution notes, and messages.";

interface MineListRow {
  id: string;
  created_at: string;
  updated_at: string;
  kind: string;
  status: string;
  page_path: string | null;
  summary: string;
  resolution_note: string | null;
  message_count: number;
}

interface MineDetailTicket {
  id: string;
  created_at: string;
  updated_at: string;
  kind: string;
  status: string;
  page_path: string | null;
  actual_behavior: string;
  expected_behavior: string;
  resolution_note: string | null;
  last_status_change_at: string | null;
}

interface MineDetailScreenshot extends HubSupportTicketAttachmentView {}

function MyHubSupportTicketListPage(): JSX.Element {
  const listQuery = useQuery({
    queryKey: ["hub-support-tickets", "mine"],
    queryFn: () => apiFetch<{ rows: MineListRow[] }>("/api/hub-support-tickets/mine"),
  });
  const rows = listQuery.data?.rows ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageTitleWithHelp hoverHint={PAGE_HELP}>My tickets</PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Submitted tickets</CardTitle>
        </CardHeader>
        <CardBody className="p-0 sm:p-0">
          {listQuery.isLoading ? (
            <p className="px-5 py-4 text-sm text-slate-500">Loading your tickets…</p>
          ) : listQuery.isError ? (
            <p className="px-5 py-4 text-sm text-red-600">{(listQuery.error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-600">
              You have not submitted any tickets yet. Use New ticket in the account menu to report a bug or leave a
              suggestion.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rows.map((row) => (
                <li key={row.id}>
                  <Link
                    to={`/personal/tickets/${encodeURIComponent(row.id)}`}
                    className="flex flex-col gap-2 px-5 py-4 transition-colors hover:bg-slate-50"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">Ticket #{row.id}</span>
                      <HubSupportTicketStatusBadge status={row.status} />
                      <span className="text-xs text-slate-500">{row.kind}</span>
                      {row.message_count > 0 ? (
                        <span className="text-xs text-slate-500">
                          {String(row.message_count)} message{row.message_count === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </div>
                    <p className="line-clamp-2 text-sm text-slate-700">{row.summary}</p>
                    <p className="text-xs text-slate-500">
                      Opened {formatTimestamp(row.created_at)}
                      {row.page_path ? (
                        <>
                          <span className="text-slate-400"> · </span>
                          <span className="font-mono">{row.page_path}</span>
                        </>
                      ) : null}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function MyHubSupportTicketDetailPage(): JSX.Element {
  const { id: ticketId = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [replyDraft, setReplyDraft] = useState("");
  const [replyError, setReplyError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ["hub-support-tickets", "mine", "detail", ticketId],
    queryFn: () =>
      apiFetch<{
        ticket: MineDetailTicket;
        screenshots: MineDetailScreenshot[];
        messages: HubSupportTicketMessage[];
      }>(`/api/hub-support-tickets/${encodeURIComponent(ticketId)}`),
    enabled: ticketId !== "",
  });

  const replyMutation = useMutation({
    mutationFn: (input: { body: string; attachments: HubSupportLocalAttachment[] }) =>
      apiFetch<{ ok: boolean; messages: HubSupportTicketMessage[] }>(
        `/api/hub-support-tickets/${encodeURIComponent(ticketId)}/messages`,
        {
          method: "POST",
          body: {
            body: input.body,
            attachments: input.attachments.map((row) => hubSupportTicketAttachmentApiPayload(row)),
          },
        },
      ),
    onSuccess: (result) => {
      setReplyDraft("");
      setReplyError(null);
      queryClient.setQueryData(
        ["hub-support-tickets", "mine", "detail", ticketId],
        (previous: { ticket: MineDetailTicket; screenshots: MineDetailScreenshot[]; messages: HubSupportTicketMessage[] } | undefined) =>
          previous ? { ...previous, messages: result.messages } : previous,
      );
      void queryClient.invalidateQueries({ queryKey: ["hub-support-tickets", "mine"] });
    },
    onError: (error: Error) => {
      setReplyError(error.message);
    },
  });

  const detail = detailQuery.data;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={() => navigate("/personal/tickets")}>
          Back to list
        </Button>
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Ticket #{ticketId}</PageTitleWithHelp>
      </header>

      <Card>
        <CardBody className="space-y-5">
          {detailQuery.isLoading && !detail ? (
            <p className="text-sm text-slate-500">Loading ticket…</p>
          ) : detailQuery.isError ? (
            <p className="text-sm text-red-600">{(detailQuery.error as Error).message}</p>
          ) : detail ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <HubSupportTicketStatusBadge status={detail.ticket.status} />
                <span className="text-sm text-slate-600">{detail.ticket.kind}</span>
                <span className="text-xs text-slate-500">Opened {formatTimestamp(detail.ticket.created_at)}</span>
              </div>
              {detail.ticket.resolution_note?.trim() ? (
                <div className="rounded-md border border-emerald-100 bg-emerald-50/80 p-3">
                  <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-emerald-800">
                    Resolution
                  </span>
                  <p className="whitespace-pre-wrap text-sm text-emerald-950">{detail.ticket.resolution_note}</p>
                </div>
              ) : null}
              <div>
                <span className={hubSupportTicketFieldLabelClassName(detail.ticket.kind)}>
                  {hubSupportTicketReporterFieldCopy(detail.ticket.kind).actualLabel}
                </span>
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md border border-slate-100 bg-slate-50 p-3 text-xs text-slate-800">
                  {detail.ticket.actual_behavior}
                </pre>
              </div>
              <div>
                <span className={hubSupportTicketFieldLabelClassName(detail.ticket.kind)}>
                  {hubSupportTicketReporterFieldCopy(detail.ticket.kind).expectedLabel}
                </span>
                <pre className="max-h-32 overflow-auto whitespace-pre-wrap rounded-md border border-slate-100 bg-slate-50 p-3 text-xs text-slate-800">
                  {detail.ticket.expected_behavior}
                </pre>
              </div>
              {detail.screenshots.length > 0 ? (
                <div>
                  <span className="mb-2 block text-xs font-medium uppercase tracking-wide text-slate-500">
                    Files
                  </span>
                  <HubSupportTicketAttachmentList attachments={detail.screenshots} />
                </div>
              ) : null}
              <HubSupportTicketMessageThread
                messages={detail.messages}
                draft={replyDraft}
                onDraftChange={setReplyDraft}
                submitting={replyMutation.isPending}
                canReply
                allowAttachments
                error={replyError}
                onSubmit={({ attachments }) => {
                  const trimmed = replyDraft.trim();
                  if (trimmed === "" && attachments.length === 0) return;
                  replyMutation.mutate({ body: trimmed, attachments });
                }}
              />
            </>
          ) : (
            <p className="text-sm text-slate-600">Ticket not found.</p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export function MyHubSupportTicketsPage(): JSX.Element {
  return (
    <Routes>
      <Route index element={<MyHubSupportTicketListPage />} />
      <Route path=":id" element={<MyHubSupportTicketDetailPage />} />
      <Route path="*" element={<Navigate to="/personal/tickets" replace />} />
    </Routes>
  );
}
